import React, { createContext, useContext, useEffect, useState } from 'react';
import { API, CurrentUser, redirectToLogout } from '../api/client';

/**
 * Phil persona model. The role is now the authenticated user's role, fetched
 * from the backend (`GET /api/auth/me`) — the DB `app_user` table is the source
 * of truth. There is no client-side role switching: to change persona, sign out
 * and sign in as another account (locally, the dev login shim lets you pick).
 */
export type PhilRole = 'applicant' | 'evaluator' | 'admin';

export const ROLE_LABELS: Record<PhilRole, string> = {
  applicant: 'Applicant (Founder)',
  evaluator: 'Evaluator (FI)',
  admin: 'Administrator (FIL)',
};

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface RoleContextValue {
  status: AuthStatus;
  role: PhilRole;
  user: CurrentUser | null;
  /** "First Last" when a name is set, else the email — never a placeholder. */
  displayName: string;
  /** Up to two initials derived from the name, else the email's first letter. */
  initials: string;
  logout: () => Promise<void>;
  saveProfile: (firstName: string, lastName: string) => Promise<void>;
}

const RoleContext = createContext<RoleContextValue>({
  status: 'loading',
  role: 'applicant',
  user: null,
  displayName: '',
  initials: '',
  logout: async () => {},
  saveProfile: async () => {},
});

/** Derive a display name + initials from a user, falling back to the email so
 *  the UI never shows a hard-coded placeholder for a nameless account. */
function deriveIdentity(user: CurrentUser | null): { displayName: string; initials: string } {
  if (!user) return { displayName: '', initials: '' };
  const first = (user.firstName || '').trim();
  const last = (user.lastName || '').trim();
  if (first || last) {
    const displayName = [first, last].filter(Boolean).join(' ');
    const initials = ((first[0] || '') + (last[0] || '')).toUpperCase();
    return { displayName, initials };
  }
  return { displayName: user.email, initials: (user.email[0] || '?').toUpperCase() };
}

export function RoleProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<CurrentUser | null>(null);

  useEffect(() => {
    let active = true;
    API.me()
      .then(me => {
        if (!active) return;
        if (me) {
          setUser(me);
          setStatus('authenticated');
        } else {
          setStatus('unauthenticated');
        }
      })
      .catch(() => {
        // 401 already triggers a login redirect in the client; mark state so the
        // gate can render a fallback if the redirect is slow.
        if (active) setStatus('unauthenticated');
      });
    return () => { active = false; };
  }, []);

  const saveProfile = async (firstName: string, lastName: string) => {
    const updated = await API.updateProfile(firstName, lastName);
    if (updated) setUser(updated);
  };

  const logout = async () => {
    // POSTs /auth/logout (CSRF-safe) to clear the app session, then navigates to
    // the returned Cognito /logout URL so the Hosted-UI session is terminated too
    // — otherwise the next login silently re-auths the same user ("sign out does
    // nothing"). See redirectToLogout in api/client.ts.
    await redirectToLogout();
  };

  const { displayName, initials } = deriveIdentity(user);

  return (
    <RoleContext.Provider
      value={{
        status,
        role: user?.role ?? 'applicant',
        user,
        displayName,
        initials,
        logout,
        saveProfile,
      }}
    >
      {children}
    </RoleContext.Provider>
  );
}

export function useRole() {
  return useContext(RoleContext);
}
